import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { postLoginPath } from '@/shared/lib/pendingIntentStorage'
import { useSessionStore } from '@/features/session/model/sessionStore'
import { ApiError, isPasskeyUserCancellation } from '@/shared/api/authApi'
import { toFriendlyErrorMessage } from '@/shared/lib/errorMessage'
import {
  isPasskeySupported,
  refreshPasskeyCapability,
} from '@/shared/lib/platform/passkeySupport'
import { Button } from '@/shared/ui/Button'
import { ErrorMessageWithCopy } from '@/shared/ui/ErrorMessageWithCopy'
import { KeyRoundIcon } from '@/shared/ui/icons'

export const PasskeyLoginButton = () => {
  const navigate = useNavigate()
  const loginWithPasskey = useSessionStore((s) => s.loginWithPasskey)
  const loading = useSessionStore((s) => s.loading)
  const [supported, setSupported] = useState<boolean | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    void refreshPasskeyCapability().then((ok) => {
      if (!cancelled) setSupported(ok)
    })
    return () => {
      cancelled = true
    }
  }, [])

  if (supported !== true && !isPasskeySupported()) {
    return null
  }

  const handlePasskeyLogin = async () => {
    setError('')
    try {
      await loginWithPasskey()
      navigate(postLoginPath(), { replace: true })
    } catch (err) {
      if (isPasskeyUserCancellation(err)) {
        return
      }
      const friendly = toFriendlyErrorMessage(
        err,
        err instanceof ApiError
          ? err.message
          : 'Não foi possível concluir o login com chave de acesso.',
      )
      const msg = /senha/i.test(friendly)
        ? friendly
        : `${friendly} Tente entrar com email e senha.`
      setError(msg)
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-center text-xs text-[var(--color-text-muted)]">
        Login com biometria ou chave de acesso — sem digitar email neste dispositivo.
      </p>

      {error ? (
        <div className="rounded-xl border border-[var(--color-wine-soft-border)] bg-[var(--color-wine-soft)] px-3 py-2">
          <ErrorMessageWithCopy message={error} textClassName="text-xs font-semibold" />
        </div>
      ) : null}

      <Button
        type="button"
        variant="secondary"
        fullWidth
        disabled={loading}
        onClick={() => void handlePasskeyLogin()}
        aria-label="Entrar com biometria ou chave de acesso"
      >
        <span className="inline-flex items-center justify-center gap-2">
          <KeyRoundIcon size={18} />
          {loading
            ? 'Entrando com biometria ou chave de acesso...'
            : 'Entrar com biometria ou chave de acesso'}
        </span>
      </Button>
    </div>
  )
}
